import {
  alignToSpeech,
  linearScale,
  shiftCues,
  speechActivityFromPcm,
  type AlignmentResult,
  type SubtitleDocument,
} from '../../core';

/**
 * Auto-resync engine (spec §4.1 #2, SP4 headline) — the framework-free glue
 * between decoded audio and the alignment core (cue-forge-5dv.5). The browser
 * decodes a local audio/video file to mono PCM on the main thread
 * (`workerRunner.ts` — `OfflineAudioContext` does not exist in dedicated
 * workers, cue-forge-5nj), never uploaded, then hands that PCM to the resync
 * Web Worker; this turns it into a speech-activity signal and asks the
 * alignment engine for the best resync, then applies it to the document.
 */

export interface ResyncOptions {
  /** Activity-signal frame period in ms (default 20 = 50 Hz). */
  msPerSample?: number;
  /** Also search for a linear rate (drift), not just a global offset. */
  estimateRate?: boolean;
}

/** Propose a resync (offset, and optionally a rate) from decoded mono PCM. */
export function resyncFromPcm(
  doc: SubtitleDocument,
  pcm: Float32Array,
  sampleRate: number,
  opts: ResyncOptions = {},
): AlignmentResult {
  const speech = speechActivityFromPcm(pcm, sampleRate, opts.msPerSample ?? 20);
  return alignToSpeech(doc, speech, { estimateRate: opts.estimateRate ?? false });
}

/**
 * Apply a proposed resync to the document: a linear retime via `linearScale`
 * when the proposal carries rate anchors, otherwise a global `shiftCues`.
 */
export function applyResync(doc: SubtitleDocument, result: AlignmentResult): SubtitleDocument {
  return result.anchors
    ? linearScale(doc, result.anchors[0], result.anchors[1])
    : shiftCues(doc, result.shiftMs);
}
